import { API } from "aws-amplify";
import { apiGateway } from "../../config";
import { store } from "../";
import { getAccounts } from "./accounts";
import { getProject, getPrompts, getQueries } from "./projects";
import { getUser } from "./user";
import { SET_SETTINGS, TOGGLE_MENU, UPDATE_CHAT_BADGE, SET_ONBOARDING_STEP, NEW_VERSION, SET_SESSION_START_TIME } from "./constants";
import { getBlocks } from "./blocks";
import { getResponses } from "./responses";
import { getSegments } from "./segments";
import { getDocuments } from "./document";
import { getAccountUsers } from "./users";
import { getContacts } from "./contacts";
import debug from "../../utilities/debug";
const Gateway = apiGateway.find((gateway) => gateway.name === "client");

export const refreshImperatives = async (projectId) => {
  const cognito_id = store.getState().authentication.user.cognito_id;
  const account_id = store.getState().session.account.account_id;
  let requests = [
    store.dispatch(getAccounts(cognito_id, account_id, true)),
    store.dispatch(getAccountUsers(account_id, cognito_id, true)),
    getUser(cognito_id, account_id, true),
  ];
  if (projectId) {
    requests.push(store.dispatch(getProject(projectId)));
    requests.push(store.dispatch(getBlocks(true)));
    requests.push(store.dispatch(getResponses(projectId, true)));
    requests.push(store.dispatch(getSegments(projectId, true)));
    requests.push(store.dispatch(getDocuments(projectId, true)));
    requests.push(store.dispatch(getPrompts(projectId, true)));
    requests.push(store.dispatch(getContacts(projectId, true)));
  }
  return await Promise.all(requests);
};

export const setOnboardingStep = (step) => async (dispatch) => {
  dispatch({ type: SET_ONBOARDING_STEP, payload: step });
};

export const checkForNewVersion = () => async (dispatch, getState) => {
  try {
    const currentVersion = getState().session.settings?.app_version;
    const response = await API.get(Gateway.name, `/users/get-settings`);
    
    if (response.success && response.payload?.app_version) {
      const newVersion = response.payload.app_version;
      
      // Compare versions - if different, dispatch NEW_VERSION with the new version
      if (newVersion !== currentVersion) {
        debug.log('ui', 'New version detected', { from: currentVersion, to: newVersion });
        dispatch({ type: SET_SETTINGS, payload: response.payload });
        dispatch({ type: NEW_VERSION, payload: newVersion });
      } else {
        debug.log('ui', 'Service worker updated but version unchanged');
      }
    }
    
    return response;
  } catch (error) {
    debug.error('Failed to check for new version', error);
    return { success: false, error };
  }
};

export const checkMaintenanceMode = async () => {
  return API.get(Gateway.name, `/users/get-settings`)
    .then((data) => {
      store.dispatch({ type: SET_SETTINGS, payload: data.payload });
      window.LOG_LEVEL = false;
      if (data.payload.debug) window.LOG_LEVEL = "DEBUG";
      return data;
    })
    .catch((error) => {
      return {
        success: false,
        message: error.response && error.response.data ? error.response.data.message : "Something went wrong."
      };
    });
};

export const toggleMenu = () => async (dispatch) => {
  dispatch({ type: TOGGLE_MENU });
};

export const updateChatBadge = (count) => async (dispatch) => {
  dispatch({ type: UPDATE_CHAT_BADGE, payload: count });
};

export const setSessionStartTime = (timestamp) => (dispatch) => {
  dispatch({ type: SET_SESSION_START_TIME, payload: timestamp });
};

export const createCompletion = async (prompt) => {
  return API.post(Gateway.name, `/prompts/create-completion/${(store.getState().session.account.account_id)}/${store.getState().authentication.user.cognito_id}`, {
    headers: {
      Authorization: store.getState().session.token,
    },
    body: {
      prompt
    },
  })
    .then((updated) => {
      return updated;
    })
    .catch((error) => {
      return {
        success: false,
        message: error.response && error.response.data ? error.response.data.message : "Something went wrong.",
      };
    });
};

export const createGeneration = async (prompt) => {
  return API.post(Gateway.name, `/prompts/generate/${(store.getState().session.account.account_id)}/${store.getState().authentication.user.cognito_id}`, {
    headers: {
      Authorization: store.getState().session.token,
    },
    body: {
      prompt
    },
  })
    .then((updated) => {
      return updated;
    })
    .catch((error) => {
      return {
        success: false,
        message: error.response && error.response.data ? error.response.data.message : "Something went wrong."
      };
    });
};

export const createResponse = async (block_id, project_id, prompt, page_id, controllerExternal, opts = {}) => {
  const state = store.getState();
  const account_id = state.session.account.account_id;
  const token = state.session.token;
  // Persist existing conversation thread if one is active. If none active but caller passes an existing query conversation id (continuing from single-turn view), reuse it.
  const existingConversationId = state.prompts?.activeConversationId;
  const normalizedExistingConversationId = (typeof existingConversationId === 'string' && existingConversationId.startsWith('pending-'))
    ? undefined
    : existingConversationId;
  const fallbackConversationId = opts?.conversation_id || undefined; // passed explicitly by UI when continuing a single historical turn
  const normalizedFallbackConversationId = (typeof fallbackConversationId === 'string' && fallbackConversationId.startsWith('pending-'))
    ? undefined
    : fallbackConversationId;
  const conversation_id = normalizedExistingConversationId || normalizedFallbackConversationId || undefined;
  // Sanitize mentions: strip metadata, keep only essential fields for backend
  const sanitizedMentions = (opts?.mentions || []).map(m => ({
    type: m.type,
    id: m.id,
    name: m.name,
    token: m.token
  }));
  const functionUrl = process.env.REACT_APP_STREAM_GENERATE_RESPONSE_URL ? `${process.env.REACT_APP_STREAM_GENERATE_RESPONSE_URL}/${account_id}/${state.authentication.user.cognito_id}` : null;
  // If no function URL configured, fallback to legacy API
  if (!functionUrl) {
    return API.post(Gateway.name, `/prompts/generate-response/${account_id}/${state.authentication.user.cognito_id}`, {
      headers: { Authorization: token },
  body: { block_id, project_id, prompt, page_id, conversation_id, mentions: sanitizedMentions, query_source: opts.query_source },
    })
      .then((updated) => {
        store.dispatch(getQueries(project_id, true));
        return updated;
      })
      .catch((error) => ({
        success: false,
        message: error.response && error.response.data ? error.response.data.message : "Something went wrong.",
        ...error?.response?.data
      }));
  }

  // Streaming path
  try {
    const controller = controllerExternal || new AbortController();
    const res = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/x-ndjson',
        'Authorization': `Bearer ${token}`
      },
  body: JSON.stringify({ account_id, block_id, project_id, prompt, page_id, conversation_id, mentions: sanitizedMentions, query_source: opts.query_source }),
      signal: controller.signal
    });
    if (!res.ok || !res.body) {
      throw new Error('Streaming request failed');
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let accumulated = '';
    let doneEvent = null;
    // Dispatch interim updates via a lightweight custom event (UI can listen)
    const emit = (detail) => {
      try { window.dispatchEvent(new CustomEvent('kb-stream', { detail })); } catch (_) {}
    };
    let errorEvent = null;
    // Buffer for incomplete NDJSON lines that span multiple reader chunks.
    // Large events (e.g. done with full tool metadata) can exceed a single TCP frame.
    let lineBuffer = '';
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      lineBuffer += decoder.decode(value, { stream: true });
      // Only process complete lines; keep any trailing fragment for the next chunk.
      const parts = lineBuffer.split('\n');
      lineBuffer = parts.pop() ?? '';
      for (const line of parts) {
        if (!line.trim()) continue;
        let evt; try { evt = JSON.parse(line); } catch { continue; }
        if (evt.type === 'delta') {
          accumulated += evt.data;
          emit({ type: 'delta', data: evt.data, full: accumulated });
        } else if (evt.type === 'done') {
          doneEvent = evt;
        } else if (evt.type === 'error') {
          errorEvent = evt;
          emit({ type: 'error', message: evt.message, errorCode: evt.errorCode, query: evt.query || null, conversation_id: evt?.query?.conversation_id || evt?.query?.conversationId || evt.conversation_id || null });
        } else if (evt.type === 'warn') {
          emit({ type: 'warn', message: evt.message });
        } else if (evt.type === 'updated_query') {
          emit({ type: 'updated_query', query: evt.query });
        } else if (evt.type === 'tool_status') {
          emit({ type: 'tool_status', message: evt.message, tool: evt.tool, details: evt.details });
        } else if (evt.type === 'tool_read') {
          emit({ type: 'tool_read', message: evt.message, tool: evt.tool });
        } else if (evt.type === 'status') {
          emit({ type: 'status', message: evt.message });
        } else if (evt.type === 'keepalive') {
          // No-op: keeps connection alive so proxies don't close the stream
        }
      }
    }
    // Flush any remaining data in the buffer (stream closed without trailing newline)
    if (lineBuffer.trim()) {
      try {
        const evt = JSON.parse(lineBuffer);
        if (evt.type === 'done') doneEvent = evt;
        else if (evt.type === 'error') { errorEvent = evt; emit({ type: 'error', message: evt.message, errorCode: evt.errorCode, query: evt.query || null, conversation_id: evt?.query?.conversation_id || evt.conversation_id || null }); }
      } catch { /* incomplete fragment — discard */ }
    }
    if (errorEvent) {
      // Always send a done event even if accumulated text exists, carrying error + query if provided
      try { window.dispatchEvent(new CustomEvent('kb-stream', { detail: { type: 'done', full: accumulated, meta: { error: errorEvent }, query: errorEvent?.query || doneEvent?.query || null, conversation_id: errorEvent?.query?.conversation_id || errorEvent?.query?.conversationId || errorEvent?.conversation_id || doneEvent?.query?.conversation_id || doneEvent?.query?.conversationId || doneEvent?.conversation_id || null } })); } catch(_) {}
      return { success: false, message: errorEvent.message, errorCode: errorEvent.errorCode, query: errorEvent?.query || null };
    }
    // Emit final done for listener with full accumulated text (success path)
    try { window.dispatchEvent(new CustomEvent('kb-stream', { detail: { type: 'done', full: accumulated, meta: doneEvent, query: doneEvent?.query || null, conversation_id: doneEvent?.query?.conversation_id || doneEvent?.query?.conversationId || doneEvent?.conversation_id || null } })); } catch(_) {}
    return {
      success: true,
      payload: accumulated,
      stream: true,
      tokens: doneEvent?.tokens,
      cost: doneEvent?.cost,
      record_id: doneEvent?.record_id,
      query: doneEvent?.query || null
    };
  } catch (e) {
  return { success: false, message: e.name === 'AbortError' ? 'Cancelled' : (e.message || 'Stream failed') };
  }
};
